import { lazy, Suspense, useCallback, useState, type ReactNode } from 'react';
import { SparklesIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@gokrew/ui/components/dialog';
import { cn } from '@gokrew/ui/lib/utils';
import styles from './Chatbot.module.css';
import { TranslationNS } from '@gokrew/models/constants';
import { useTranslation } from 'react-i18next';
import { AssistantContext, useOpenAssistant } from './assistantContext';

const AiAssistantPage = lazy(() =>
  import('@/pages/ai-assistant/AiAssistantPage').then((m) => ({
    default: m.AiAssistantPage,
  }))
);

/**
 * Owns the full-screen AI Assistant dialog, so the side tab (desktop) and
 * the header button (phone) open the same one.
 */
export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation(TranslationNS.chatbot);
  const open = useCallback(() => setIsOpen(true), []);

  return (
    <AssistantContext.Provider value={open}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className={styles.fullscreenDialog}
          bodyClass={styles.fullscreenDialogBody}
          showCloseButton={false}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{t('sidebar.header')}</DialogTitle>
          <Suspense fallback={null}>
            <AiAssistantPage onClose={() => setIsOpen(false)} />
          </Suspense>
        </DialogContent>
      </Dialog>
    </AssistantContext.Provider>
  );
};

/**
 * The vertical "AI Assistant" tab on the right edge. Below 48rem it would
 * cover the page's right edge, so it hides there and the header's
 * `AssistantHeaderButton` takes over (UX review 2026-10-02).
 */
export const AssistantLauncher = () => {
  const open = useOpenAssistant();
  const { t } = useTranslation(TranslationNS.chatbot);
  if (!open) return null;

  return (
    <button type="button" className={styles.launcher} onClick={open}>
      <SparklesIcon className={styles.launcherSparkle} aria-hidden="true" />
      <span>{t('launcher.buttonLabel')}</span>
    </button>
  );
};

/** Phone-width stand-in for the side tab, a round button in the header. */
export const AssistantHeaderButton = ({
  className,
}: {
  className?: string;
}) => {
  const open = useOpenAssistant();
  const { t } = useTranslation(TranslationNS.chatbot);
  if (!open) return null;

  return (
    <button
      type="button"
      className={cn(styles.headerLauncher, className)}
      onClick={open}
      aria-label={t('launcher.title')}
      title={t('launcher.title')}
    >
      <SparklesIcon size={18} aria-hidden="true" />
    </button>
  );
};
